import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Button,
  Select,
  Space,
  Table,
  Typography,
  Row,
  Col,
  Spin,
  notification,
  Divider,
  Form,
  Input,
  InputNumber,
  Tag,
  Popconfirm,
  Tooltip,
} from "antd";
import {
  ArrowLeftOutlined,
  SaveOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  getTransactionById,
  getAllPartners,
  getAccountTree,
} from "../../api/accounting";
import {
  createOverpaymentFromTransaction,
  getAllOverpayments,
  deleteOverpayment,
} from "../../api/overpayment";
import "bootstrap/dist/css/bootstrap.min.css";
import "../Shared/Utils/BankStatements.css"; // reusing the same CSS for consistent styling
// Import column definitions
import getTransactionColumns from "../Shared/Columns/PaymentAllocation/transactionColumns";

const { Text, Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

// Helper function to render hierarchical account options
const renderAccountOptions = (accounts) => {
  if (!accounts || !accounts.length) return null;

  // Build Option items for a subtree (no nested groups here)
  const buildIndentedOptions = (items, depth = 0, includeSelf = true) => {
    return items.flatMap((account) => {
      const hasChildren =
        Array.isArray(account.children) && account.children.length > 0;
      const indent = depth > 0 ? `${"— ".repeat(depth)}` : ""; // — repeated per depth

      const nodes = [];
      // Selectability: postable or leaf
      const selectable = !!(account.is_postable || !hasChildren);
      const label = account.code
        ? `${account.code} - ${account.name}`
        : account.name;

      // Include the node itself as an option (disabled if not selectable)
      if (includeSelf) {
        nodes.push(
          <Select.Option
            key={account.id}
            value={account.id}
            disabled={!selectable}
          >
            {`${indent}${label}`}
          </Select.Option>
        );
      }

      if (hasChildren) {
        nodes.push(...buildIndentedOptions(account.children, depth + 1, true));
      }
      return nodes;
    });
  };

  try {
    // Render only one OptGroup level (top-level accounts) to avoid nested groups
    return accounts.map((root) => (
      <Select.OptGroup
        key={root.id}
        label={`${root.code || ""} - ${root.name}`}
      >
        {/* Do not render the root as an option to avoid duplication with the group label */}
        {buildIndentedOptions([root], 0, false)}
      </Select.OptGroup>
    ));
  } catch (error) {
    console.error("Error rendering account options:", error);
    return null;
  }
};

const OverPaymentPage = ({
  transactionId,
  selectedBrand,
  selectedBrands: selectedBrandsProp,
  onSuccess,
  onCancel,
}) => {
  // Support both single brand and multiple brands
  const selectedBrands =
    selectedBrandsProp || (selectedBrand ? [selectedBrand] : []);
  const primaryBrand = selectedBrands.length > 0 ? selectedBrands[0] : null;
  const navigate = useNavigate();
  const [loadingTransaction, setLoadingTransaction] = useState(false);
  const [loadingOverpayments, setLoadingOverpayments] = useState(false);
  const [transaction, setTransaction] = useState(null);
  const [allocationStatus, setAllocationStatus] = useState(null);
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [amount, setAmount] = useState(null);
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [overpayments, setOverpayments] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [form] = Form.useForm();
  // Fetch overpayments for this transaction
  const fetchOverpayments = useCallback(async () => {
    if (!transactionId) return;

    setLoadingOverpayments(true);
    try {
      const filters = {
        transaction: transactionId,
      };

      // Add brand filter if available - support multiple brands
      if (selectedBrands && selectedBrands.length > 0) {
        filters.brand = selectedBrands.map((b) => b.id).join(",");
      }

      const response = await getAllOverpayments(filters);
      setOverpayments(response || []);
    } catch (error) {
      console.error("Error fetching overpayments:", error);
      notification.error({
        message: "Error",
        description: "Failed to load overpayments for this transaction",
      });
    } finally {
      setLoadingOverpayments(false);
    }
  }, [transactionId, selectedBrands]);

  // Handle back button
  const handleBack = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      navigate("/accounting/bank-statements");
    }
  }, [navigate, onCancel]);

  // Fetch transaction details
  const fetchTransaction = useCallback(async () => {
    setLoadingTransaction(true);
    try {
      const data = await getTransactionById(transactionId);
      setTransaction(data);
      const amount_total = Math.abs(parseFloat(data.amount || 0));

      setAmount(amount_total);
      form.setFieldsValue({ amount: amount_total });

      // Update allocation status using data from backend
      updateAllocationStatus(data);

      // If transaction has a partner, pre-select it
      if (data.partner) {
        setSelectedPartner(data.partner);
      }
    } catch (error) {
      console.error("Error fetching transaction:", error);

      // Check if it's a 404 error (transaction not found)
      if (error.response && error.response.status === 404) {
        notification.error({
          message: "Transaction Not Found",
          description: `Transaction with ID ${transactionId} does not exist.`,
        });
        // Navigate back to bank statements after a delay
        setTimeout(() => {
          handleBack();
        }, 2000);
      } else {
        notification.error({
          message: "Error",
          description: "Failed to load transaction details",
        });
      }
    } finally {
      setLoadingTransaction(false);
    }
  }, [transactionId, handleBack]);

  // Fetch transaction data
  useEffect(() => {
    if (!transactionId) return;
    fetchTransaction();
    fetchOverpayments();
  }, [transactionId, fetchTransaction]);

  // Fetch all partners
  useEffect(() => {
    if (!selectedBrands || selectedBrands.length === 0 || !transaction) return;

    const fetchPartners = async () => {
      try {
        // Determine partner type filter based on transaction amount
        let partnerTypeFilter = null;
        const transactionAmount = parseFloat(transaction.amount || 0);

        if (transactionAmount > 0) {
          // For positive transactions (money in), show only customers
          partnerTypeFilter = "customer";
        } else if (transactionAmount < 0) {
          // For negative transactions (money out), show only vendors
          partnerTypeFilter = "vendor";
        }

        // Fetch partners for all selected brands
        const brandIds = selectedBrands.map((b) => b.id).join(",");
        const data = await getAllPartners({
          brand: brandIds,
          type: partnerTypeFilter, // Filter by partner type based on transaction amount
        });

        // Deduplicate partners by ID (same partner can exist across multiple brands)
        const rawPartnersList = Array.isArray(data.data) ? data.data : [];
        const seenIds = new Set();
        const seenNames = new Set();
        const partnersList = rawPartnersList.filter((partner) => {
          if (seenIds.has(partner.id)) {
            return false;
          }
          const normalizedName = (partner.name || "").toLowerCase().trim();
          if (seenNames.has(normalizedName)) {
            return false;
          }
          seenIds.add(partner.id);
          seenNames.add(normalizedName);
          return true;
        });
        setPartners(partnersList);
      } catch (error) {
        console.error("Error fetching partners:", error);
        // Initialize as empty array on error
        setPartners([]);
      }
    };

    fetchPartners();
  }, [selectedBrands, transaction]);

  // Fetch accounts
  useEffect(() => {
    if (!primaryBrand) return;

    const fetchAccounts = async () => {
      try {
        const filters = { brand: primaryBrand.id };
        const response = await getAccountTree(filters);
        setAccounts(response);
      } catch (error) {
        console.error("Error fetching accounts:", error);
        setAccounts([]);
      }
    };

    fetchAccounts();
  }, [primaryBrand]);

  // Handle partner change
  const handlePartnerChange = (value) => {
    setSelectedPartner(value);
    form.setFieldsValue({ partner: value });
  };

  // Handle account change
  const handleAccountChange = (value) => {
    setSelectedAccount(value);
    form.setFieldsValue({ account: value });
  };

  // Handle overpayment creation
  const handleCreateOverpayment = async () => {
    try {
      // Validate form
      await form.validateFields();

      const amountValue = Number(amount);

      if (
        !selectedPartner ||
        !selectedAccount ||
        Number.isNaN(amountValue) ||
        amountValue <= 0
      ) {
        notification.error({
          message: "Validation Error",
          description:
            "Please select a partner, account, and enter a valid amount",
        });
        return;
      }

      setCreating(true);

      const data = {
        transaction_id: transactionId,
        partner_id: selectedPartner,
        amount: amountValue,
        notes: notes,
        brand_id: primaryBrand?.id || selectedBrand?.id,
        account_id: selectedAccount,
      };

      const response = await createOverpaymentFromTransaction(data);

      notification.success({
        message: "Success",
        description: "Overpayment created successfully",
      });

      // Refresh transaction data and overpayments
      fetchTransaction();
      fetchOverpayments();

      // Reset form
      form.resetFields(["amount", "notes"]);
      setAmount(null);
      setNotes("");

      // Call onSuccess callback if provided (for modal usage)
      // Pass the newly created overpayment ID so parent can open allocation
      if (onSuccess) {
        // The API response should include overpayment_id
        const newOverpaymentId =
          response?.overpayment_id || response?.id || null;
        onSuccess(newOverpaymentId);
      }
    } catch (error) {
      console.error("Error creating overpayment:", error);
      notification.error({
        message: "Error",
        description:
          error.response?.data?.error || "Failed to create overpayment",
      });
    } finally {
      setCreating(false);
    }
  };

  const updateAllocationStatus = (transaction) => {
    if (!transaction) {
      setAllocationStatus(null);
      return;
    }

    try {
      if (transaction.allocation_status) {
        setAllocationStatus({
          ...transaction.allocation_status,
          totalAllocated: parseFloat(transaction.allocated_amount || 0),
          percentAllocated: transaction.percent_allocated || 0,
        });
        return;
      }

      const transactionAmount = Math.abs(parseFloat(transaction.amount || 0));
      const totalAllocated = parseFloat(transaction.allocated_amount || 0);
      const percentAllocated =
        transactionAmount > 0 ? (totalAllocated / transactionAmount) * 100 : 0;
      let status = null;
      if (percentAllocated >= 99.99) {
        status = {
          type: "fully_allocated",
          label: "Fully Allocated",
          color: "success",
        };
      } else if (percentAllocated > 0) {
        status = {
          type: "partially_allocated",
          label: "Partially Allocated",
          color: "warning",
        };
      } else {
        status = {
          type: "unallocated",
          label: "Unallocated",
          color: "default",
        };
      }

      setAllocationStatus({
        ...status,
        totalAllocated,
        percentAllocated,
      });
    } catch (error) {
      console.error("Error updating allocation status:", error);
      setAllocationStatus({
        type: "error",
        label: "Error",
        color: "error",
        totalAllocated: 0,
        percentAllocated: 0,
      });
    }
  };

  // Get transaction columns from the imported function
  const transactionColumns = getTransactionColumns(allocationStatus);

  // Handle overpayment deletion
  const handleDeleteOverpayment = async (overpaymentId) => {
    setDeleting(true);
    try {
      await deleteOverpayment(overpaymentId);

      notification.success({
        message: "Success",
        description: "Overpayment deleted successfully",
      });

      // Refresh overpayments list
      fetchOverpayments();
      // Also refresh transaction data as the remaining amount might have changed
      fetchTransaction();
    } catch (error) {
      console.error("Error deleting overpayment:", error);

      // Check if it's a specific error related to allocations
      const errorMessage =
        error.response?.data?.error || "Failed to delete overpayment";

      notification.error({
        message: "Error",
        description: errorMessage,
      });
    } finally {
      setDeleting(false);
    }
  };

  // Define columns for overpayments table
  const overpaymentColumns = [
    {
      title: "Partner",
      dataIndex: "partner_name",
      key: "partner_name",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (text) => (
        <span>
          {parseFloat(text).toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
          })}
        </span>
      ),
    },
    // {
    //   title: 'Remaining Amount',
    //   dataIndex: 'remaining_amount',
    //   key: 'remaining_amount',
    //   render: (text) => (
    //     <span>
    //       {parseFloat(text).toLocaleString('en-US', {
    //         style: 'currency',
    //         currency: 'USD',
    //         minimumFractionDigits: 2,
    //       })}
    //     </span>
    //   ),
    // },
    {
      title: "Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => new Date(text).toLocaleDateString(),
    },
    // {
    //   title: 'Status',
    //   dataIndex: 'remaining_amount',
    //   key: 'status',
    //   render: (text, record) => {
    //     const remainingAmount = parseFloat(text || 0);
    //     const totalAmount = parseFloat(record.amount || 0);

    //     if (remainingAmount <= 0) {
    //       return <Tag color="green">Fully Allocated</Tag>;
    //     } else if (remainingAmount < totalAmount) {
    //       return <Tag color="orange">Partially Allocated</Tag>;
    //     } else {
    //       return <Tag color="blue">Unallocated</Tag>;
    //     }
    //   },
    // },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      ellipsis: true,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        // Check if overpayment can be deleted (only if remaining amount equals total amount)
        const remainingAmount = parseFloat(record.remaining_amount || 0);
        const totalAmount = parseFloat(record.amount || 0);
        const canDelete = Math.abs(remainingAmount - totalAmount) < 0.01; // Using small epsilon for floating point comparison

        return (
          <Space>
            {canDelete ? (
              <Popconfirm
                title="Delete Overpayment"
                description="Are you sure you want to delete this overpayment?"
                onConfirm={() => handleDeleteOverpayment(record.id)}
                okText="Yes"
                cancelText="No"
                okButtonProps={{ loading: deleting }}
              >
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  size="small"
                ></Button>
              </Popconfirm>
            ) : (
              <Tooltip title="Cannot delete overpayment that has been allocated">
                <Button
                  type="link"
                  icon={<DeleteOutlined />}
                  size="small"
                  disabled
                ></Button>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  if (!transactionId || (!selectedBrand && !primaryBrand)) {
    return (
      <Card title="Overpayment">
        <Text type="secondary">
          No transaction selected or brand not selected
        </Text>
        <div style={{ marginTop: 16 }}>
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
          >
            Back to Bank Statements
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={
        <Space>
          <span>Overpayment</span>
        </Space>
      }
      size="small"
      className="payment-allocation-card"
    >
      {loadingTransaction ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Title level={5}>Transaction Details</Title>
          {transaction && (
            <Table
              dataSource={[transaction]}
              columns={transactionColumns}
              pagination={false}
              rowKey="id"
              size="small"
            />
          )}

          <Divider />

          <Divider orientation="left">Existing Overpayments</Divider>

          {loadingOverpayments ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Spin size="small" />
            </div>
          ) : overpayments.length > 0 ? (
            <Table
              dataSource={overpayments}
              columns={overpaymentColumns}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 5 }}
              loading={deleting}
            />
          ) : (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Text type="secondary">
                No overpayments found for this transaction
              </Text>
            </div>
          )}

          <Divider orientation="left">Create New Overpayment</Divider>

          <Form form={form} layout="vertical">
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Select Partner"
                  name="partner"
                  rules={[
                    { required: true, message: "Please select a partner" },
                  ]}
                  initialValue={selectedPartner}
                >
                  <Select
                    placeholder="Select a partner"
                    style={{ width: "100%" }}
                    onChange={handlePartnerChange}
                    value={selectedPartner}
                    showSearch
                    optionFilterProp="children"
                  >
                    {Array.isArray(partners) && partners.length > 0 ? (
                      partners.map((partner) => (
                        <Option key={partner.id} value={partner.id}>
                          {partner.name} ({partner.partner_type})
                        </Option>
                      ))
                    ) : (
                      <Option value="" disabled>
                        No partners available
                      </Option>
                    )}
                  </Select>
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  label="Overpayment Amount"
                  name="amount"
                  rules={[
                    { required: true, message: "Please enter an amount" },
                    {
                      validator: (_, value) => {
                        if (value === undefined || value === null) {
                          return Promise.reject(
                            new Error("Amount must be greater than zero")
                          );
                        }
                        const numericValue =
                          typeof value === "string"
                            ? parseFloat(value)
                            : Number(value);
                        if (Number.isNaN(numericValue) || numericValue <= 0) {
                          return Promise.reject(
                            new Error("Amount must be greater than zero")
                          );
                        }
                        // Enforce client-side cap to remaining transaction amount
                        const remainingAbs = Math.abs(
                          Number(transaction?.remaining_amount || 0)
                        );
                        if (remainingAbs > 0 && numericValue > remainingAbs) {
                          return Promise.reject(
                            new Error(
                              `Amount exceeds remaining transaction amount (${
                                Math.round(remainingAbs * 100) / 100
                              })`
                            )
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    placeholder="Enter amount"
                    formatter={(value) =>
                      value !== null && value !== undefined
                        ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                        : ""
                    }
                    parser={(value) =>
                      value ? value.replace(/[^\d.-]/g, "") : ""
                    }
                    onChange={(value) =>
                      setAmount(
                        value === null || value === undefined
                          ? null
                          : Number(value)
                      )
                    }
                    min={0.01}
                    max={
                      transaction
                        ? Math.abs(
                            Number(transaction?.remaining_amount || 0)
                          ) || undefined
                        : undefined
                    }
                    step={0.01}
                    precision={2}
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  label="Chart of Account"
                  name="account"
                  rules={[
                    { required: true, message: "Please select an account" },
                  ]}
                >
                  <Select
                    placeholder="Select account"
                    style={{ width: "100%" }}
                    value={selectedAccount}
                    allowClear
                    onChange={handleAccountChange}
                    showSearch
                    optionFilterProp="children"
                  >
                    {accounts && renderAccountOptions(accounts)}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item label="Notes" name="notes">
                  <TextArea
                    rows={4}
                    placeholder="Enter notes"
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col span={24} style={{ textAlign: "right" }}>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleCreateOverpayment}
                  loading={creating}
                  disabled={
                    !selectedPartner ||
                    !selectedAccount ||
                    amount === null ||
                    Number(amount) <= 0
                  }
                >
                  Create Overpayment
                </Button>
              </Col>
            </Row>
          </Form>
        </>
      )}
    </Card>
  );
};

export default OverPaymentPage;
